import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin } from 'lucide-react';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import { businesses, companyInfo, leadership } from '../mockData';

const values = [
  { n: '01', t: 'Discipline', d: 'Repeatable, documented operating processes across every subsidiary — measured, reviewed, improved.' },
  { n: '02', t: 'Transparency', d: 'Honest pricing, honest timelines. If something slips, we say so first — with a corrective plan attached.' },
  { n: '03', t: 'Global sensibility', d: 'Offices in India and North America. Every mandate is served with a genuine 24-hour operating envelope.' },
  { n: '04', t: 'Client-side judgement', d: 'We measure ourselves by client outcomes over multi-year horizons, not quarterly renewal metrics.' }
];

const AboutPage = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Lockene',
    description: companyInfo.description,
    url: 'https://lockene.info/about',
    about: {
      '@type': 'Organization',
      name: companyInfo.legalName,
      foundingDate: companyInfo.founded
    }
  };

  return (
    <>
      <Seo
        title="About Lockene · A Global Holding Company"
        description={companyInfo.description}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-brand-alabaster pt-28 md:pt-36 pb-16" aria-labelledby="about-heading" data-testid="about-hero">
        <div className="container-editorial">
          <div className="border-t border-brand-hairline mb-14" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">About Lockene</span>
              </div>
              <h1 id="about-heading" className="font-display text-5xl md:text-7xl leading-[1.03] tracking-tight text-brand-oxford">
                Built to be judged over decades, not quarters.
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-lg text-brand-slate leading-relaxed">
                Lockene was founded in {companyInfo.founded} as a disciplined holding structure for four independently
                operated businesses. The parent exists to enforce a common standard — not to interfere with subsidiary judgement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial narrative */}
      <section className="bg-white border-y border-brand-hairline py-24 md:py-32" aria-labelledby="story-heading">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">The Firm</span>
            </div>
            <h2 id="story-heading" className="font-display text-3xl md:text-4xl leading-tight text-brand-oxford">
              A holding company organised around operating discipline.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6 text-lg text-brand-slate leading-relaxed">
            <p>
              Lockene is a diversified holding company operating four independent businesses across enterprise AI software,
              government compliance, sports &amp; hospitality and premium real estate. Each subsidiary carries its own
              leadership, its own P&amp;L and its own operating cadence.
            </p>
            <p>
              What the parent enforces is a single standard: senior operators on every mandate, transparent pricing and
              timelines, documented client outcomes and a bench that spans India and the United States.
            </p>
            <p>
              The result is a firm that behaves less like a conglomerate and more like a disciplined operating platform
              — where trust is a byproduct of behaviour, not of tagline.
            </p>
          </div>
        </div>
      </section>

      {/* Values grid */}
      <section className="bg-brand-alabaster py-24 md:py-32" aria-labelledby="values-heading">
        <div className="container-editorial">
          <Reveal className="max-w-2xl mb-16">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">Operating Principles</span>
            </div>
            <h2 id="values-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
              What we hold ourselves to, without exception.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {values.map((v, i) => (
              <Reveal key={v.n} delay={i * 70}>
                <div className="bg-white border border-brand-hairline p-8 h-full">
                  <div className="font-display text-4xl text-brand-gold mb-4">{v.n}</div>
                  <h3 className="text-lg font-semibold text-brand-oxford mb-3">{v.t}</h3>
                  <p className="text-brand-slate leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership / Founders */}
      <section className="section bg-brand-alabaster border-t border-brand-hairline" aria-labelledby="lead-heading" data-testid="about-leadership">
        <div className="container-editorial">
          <Reveal className="max-w-2xl mb-16">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">Leadership</span>
            </div>
            <h2 id="lead-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
              Meet the founders.
            </h2>
            <p className="mt-6 text-lg text-brand-slate leading-relaxed">
              Lockene was founded in {companyInfo.founded} by two operators who between them run the
              regulatory practice, the AI CRM platform, the sports &amp; hospitality brand and the real-estate advisory.
              They remain personally involved in every material engagement.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {leadership.map((p, i) => (
              <Reveal key={p.name} delay={i * 90}>
                <article className="bg-white border border-brand-hairline grid grid-cols-1 md:grid-cols-5 h-full">
                  <div className="md:col-span-2 relative overflow-hidden bg-brand-alabaster">
                    <img src={p.image} alt={p.name} className="w-full h-72 md:h-full object-cover object-top" loading="lazy" />
                  </div>
                  <div className="md:col-span-3 p-8 md:p-10 flex flex-col">
                    <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-gold">{p.role}</div>
                    <h3 className="font-display text-3xl text-brand-oxford mt-2">{p.name}</h3>
                    <p className="mt-4 text-brand-slate leading-relaxed">{p.bio}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {p.expertise.map((tag) => (
                        <span key={tag} className="text-[11px] tracking-[0.14em] uppercase font-semibold text-brand-slate border border-brand-hairline px-2.5 py-1">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <a
                      href={p.linkedin}
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid={`about-lead-linkedin-${p.name.replace(/\W+/g, '-').toLowerCase()}`}
                      className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-oxford border-b border-brand-oxford pb-1 self-start hover:gap-3 transition-all"
                    >
                      <Linkedin size={14} strokeWidth={1.5} /> Connect on LinkedIn
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Global presence */}
      <section className="bg-white border-y border-brand-hairline py-24 md:py-32" aria-labelledby="offices-heading">
        <div className="container-editorial">
          <Reveal className="max-w-2xl mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">Global Bench</span>
            </div>
            <h2 id="offices-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
              Four offices. One operating envelope.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {companyInfo.locations.map((loc, i) => (
              <Reveal key={loc.city} delay={i * 60}>
                <div className="border border-brand-hairline p-8">
                  <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80">{loc.role}</div>
                  <div className="font-display text-4xl text-brand-oxford mt-3">{loc.city}</div>
                  <div className="text-sm text-brand-slate mt-1">{loc.country}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio quick-nav */}
      <section className="bg-brand-alabaster py-24 md:py-32" aria-labelledby="port-heading">
        <div className="container-editorial">
          <Reveal className="max-w-2xl mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">The Portfolio</span>
            </div>
            <h2 id="port-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
              Four subsidiaries, each with its own thesis.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {businesses.map((b, i) => (
              <Reveal key={b.id} delay={i * 60}>
                <Link
                  to={`/business/${b.id}`}
                  data-testid={`about-portfolio-${b.id}`}
                  className="group block bg-white border border-brand-hairline hover:shadow-lift transition-shadow duration-300"
                >
                  <div className="h-1" style={{ backgroundColor: `rgb(var(--sub-${b.subBrand}))` }} aria-hidden />
                  <div className="p-8 md:p-10 flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[11px] tracking-[0.16em] uppercase font-bold" style={{ color: `rgb(var(--sub-${b.subBrand}))` }}>
                        {b.tagline}
                      </div>
                      <div className="font-display text-3xl text-brand-oxford mt-3">{b.name}</div>
                      <p className="text-brand-slate mt-3 leading-relaxed max-w-md">{b.description}</p>
                    </div>
                    <span className="text-brand-oxford text-2xl group-hover:translate-x-1 transition-transform" aria-hidden>→</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
